import React, { useLayoutEffect, useRef, useEffect, forwardRef, useState } from 'react';
import FocusLock from 'react-focus-lock';

import { getFocusableArray } from './selectors';
import FocuscontainerProps from './types';
import useFocusable from './useFocusable';

import './focus-container.scss';

const FocusContainer = forwardRef<HTMLDivElement, FocuscontainerProps>(
    (
        {
            entry,
            onEnter = () => null,
            onExit,
            timeOut,
            className,
            children,
            useExtraKeyboardFocusCommands = false,
            focusLastElementFirst = false,
            elementToFocusFirst,
            returnFocus = true,
            setEntryUponFocusInside = false,
            autoFocus = true,
            disableEscapeKeyDown = false,
            ...rest
        },
        forwardedRef,
    ) => {
        const focusContainer = useFocusable(entry, onExit, useExtraKeyboardFocusCommands, disableEscapeKeyDown);

        const timeoutRef = useRef<NodeJS.Timeout | number | undefined>(undefined);

        const [focusOnLoad, setFocusOnLoad] = useState(false);

        useEffect(() => {
            if (entry && timeOut) {
                timeoutRef.current = setTimeout(onExit, timeOut);
            } else if (!entry) {
                clearTimeout(timeoutRef.current as number);
            }
            return () => clearTimeout(timeoutRef.current as number);
        }, [entry, onExit, timeOut]);

        useLayoutEffect(() => {
            setFocusOnLoad(true);
        }, []);

        return (
            <FocusLock
                className={className}
                disabled={!entry}
                returnFocus={returnFocus}
                ref={forwardedRef}
                autoFocus={autoFocus}
                onActivation={(el) => {
                    if (focusOnLoad) {
                        setFocusOnLoad(false);
                        if (!autoFocus) {
                            focusContainer.current?.focus();
                            return;
                        }
                        const focusable = getFocusableArray(el);
                        if (focusLastElementFirst && focusable.length > 0) {
                            focusable[focusable.length - 1]?.focus();
                            return;
                        }
                        const firstToTocus = focusable.find((x) => x.dataset.focusFirst) ?? elementToFocusFirst;
                        if (firstToTocus) {
                            firstToTocus.focus();
                        }
                    }
                }}
                {...rest}
            >
                <div
                    data-testid="focus-container"
                    id="focus-container"
                    ref={focusContainer}
                    tabIndex={-1}
                    onFocus={
                        setEntryUponFocusInside
                            ? (e) => {
                                  if (!entry && focusContainer.current?.contains(e.target)) {
                                      onEnter();
                                  }
                              }
                            : undefined
                    }
                >
                    {children}
                </div>
            </FocusLock>
        );
    },
);

export default FocusContainer;
